import dig from "object-dig";
import {
  each,
  map
} from "lodash";
import { getDifferenceInMinutes, toISOLocal, changeTimeZone } from "./dateHelpers";


const nullCheck = (object, nullValue, ...keys) => {
  const value = dig(object, ...keys);

  if (value !== null && typeof value !== "undefined" && value !== "") {
    return value;
  } else {
    return nullValue;
  }
};

const removeDuplicatesFromArray = array => {
  return [...new Set(array)];
};

const cleanData = text => {
  function cleanData(data) {
    //Handle MO
    //If MO is the first state, it is assumed to be a state
    //Otherwise, it is assumed to be a time
    each(data.states, function (s, idx) {
      if (idx === 0) return;
      if (s === "MO") {
        data.states.splice(idx, 1);
      }
    });

    // Check for Washington DC. Necessary because of Washington State
    // No need to check first element as it cannot have Washington before it
    for (var idx = 1; idx < data.states.length; idx++) {
      if (
        data.states[idx] === "DC" &&
        data.cities[idx] !== "WASHINGTON" &&
        /^WASHINGTON$|^WA$/.test(data.states[idx - 1])
      ) {
        // Make washington a city, and remove it from the states
        data.cities.splice(idx, 0, "WASHINGTON");
        data.states.splice(idx - 1, 1);
        idx--;
      }
    }

    for (idx = 0; idx < data.states.length; idx++) {
      if (data.states[idx] === "MT" && !data.cities[idx]) {
        data.states.splice(idx, 1);
      }
    }

    data.cities = map(data.cities, function (c) {
      return c.replace(/\s+/, " ").replace(/[^A-Za-z0-9\s_]+/, "");
    });

    return data;
  }

  return map(text, cleanData);
};

const isDate = string => {
  if (string) {
    let regexFull = /(?:(?:31(\/|-|\.)(?:0?[13578]|1[02]))\1|(?:(?:29|30)(\/|-|\.)(?:0?[13-9]|1[0-2])\2))(?:(?:1[6-9]|[2-9]\d)?\d{2})$|^(?:29(\/|-|\.)0?2\3(?:(?:(?:1[6-9]|[2-9]\d)?(?:0[48]|[2468][048]|[13579][26])|(?:(?:16|[2468][048]|[3579][26])00))))$|^(?:0?[1-9]|1\d|2[0-8])(\/|-|\.)(?:(?:0?[1-9])|(?:1[0-2]))\4(?:(?:1[6-9]|[2-9]\d)?\d{2})/;
    let regexShort = /([1])?(\d)(\/|-|\.)([1-3])?([0-9])/;
    return regexFull.test(string) || regexShort.test(string);
  } else {
    return false;
  }
};

const getEtaStatusHoverString = (etaStatus, exceptions) => {
  let etaStatusHoverString = "";
  const hasExceptions = (exceptions != null) && (
    exceptions.missingDispatchStatus ||
    exceptions.missingArrivalTime ||
    exceptions.missingDepartureTime ||
    exceptions.missingTrailerException ||
    exceptions.missingTractorException
  );
  switch (etaStatus) {
    case -1:
    case 1:
      if (hasExceptions)
        etaStatusHoverString = "Exception-only";
      break;
    case 2:
      etaStatusHoverString = "At Risk";
      if (hasExceptions)
        etaStatusHoverString += " + Exception";
      break;
    case 3:
      etaStatusHoverString = "Late";
      if (hasExceptions)
        etaStatusHoverString += " + Exception";
      break;
  }
  if (exceptions?.missingDispatchStatus)
    etaStatusHoverString += "\n" + "Not in Dispatch Status";
  if (exceptions?.missingArrivalTime)
    etaStatusHoverString += "\n" + "Missing Arrival Time";
  if (exceptions?.missingDepartureTime)
    etaStatusHoverString += "\n" + "Missing Departure Time";
  if (exceptions?.missingTrailerException)
    etaStatusHoverString += "\n" + "Missing Trailer Number";
  if (exceptions?.missingTractorException)
    etaStatusHoverString += "\n" + "Missing Tractor Number";
  return etaStatusHoverString;
};

const getLoadExceptions = (load) => {
  const currentTime = toISOLocal(changeTimeZone(new Date(), "America/Chicago")).replace("Z", "");
  const isAppointment = load.currentStopSchedArriveLate === null || load.currentStopSchedArriveEarly === load.currentStopSchedArriveLate;
  const calculateDiff = (early, late) => {
    return late && early !== late ? getDifferenceInMinutes(late, currentTime) : getDifferenceInMinutes(early, currentTime);
  };

  const missingArrivalTime = (originSchedArriveEarly, originSchedArriveLate) => {
    if (!load.currentStopActualArrival) {
      return calculateDiff(originSchedArriveEarly, originSchedArriveLate) < 0;
    }
    return false;
  };

  const missingDepartureTime = () => {
    if (load.currentStopActualArrival && !load.currentStopActualDeparture) {
      const arrivedearly = getDifferenceInMinutes(load.currentStopActualArrival, load.currentStopSchedArriveEarly) > 0;
      let compareTime;
      if (isAppointment || !arrivedearly) {
        compareTime = load.currentStopSchedArriveEarly;
      } else {
        compareTime = load.currentStopActualArrival;
      }
      return getDifferenceInMinutes(currentTime, compareTime) > 120;
    }
    return false;
  };

  const missingTrailerException = (brokerageStatus, carrierTrailer) => {
    switch (brokerageStatus) {
      case "DIS":
      case "LOA":
      case "LOAD":
      case "DEL":
        return !carrierTrailer;
      default:
        return false;
    }
  };

  const missingTractorException = (brokerageStatus, carrierTractor) => {
    switch (brokerageStatus) {
      case "DIS":
      case "LOA":
      case "LOAD":
      case "DEL":
        return !carrierTractor;
      default:
        return false;
    }
  };

  const missingDispatchStatus = (originSchedArriveEarly, originSchedArriveLate) => {
    if (load.brokerageStatus === "COV") {
      return calculateDiff(originSchedArriveEarly, originSchedArriveLate) < 180;
    }
    return false;
  };

  const exceptions = {
    missingDispatchStatus: missingDispatchStatus(load.originSchedArriveEarly, load.originSchedArriveLate),
    missingArrivalTime: missingArrivalTime(load.currentStopSchedArriveEarly, load.currentStopSchedArriveLate),
    missingDepartureTime: missingDepartureTime(),
    missingTrailerException: missingTrailerException(load.brokerageStatus, load.carrierTrailer),
    missingTractorException: missingTractorException(load.brokerageStatus, load.carrierTractor)
  };

  const hasExceptions = (exceptions != null) && (
    exceptions.missingDispatchStatus ||
    exceptions.missingArrivalTime ||
    exceptions.missingDepartureTime ||
    exceptions.missingTrailerException ||
    exceptions.missingTractorException
  );
  if (hasExceptions)
    return exceptions;
  return null;
};

// Hint. 1=On Time, 2=At Risk, 3=Late, 4=Exception. See useEtaStatus.js
const getEtaStatus = (
  brokerageStatus,
  newEta,
  originSchedArriveEarly,
  originSchedArriveLate,
  destinationSchedArriveEarly,
  destinationSchedArriveLate
) => {
  if (!newEta) {
    return -1;
  }

  let diff;
  const calculateDiff = (early, late) => {
    return late && early !== late ? getDifferenceInMinutes(late, newEta) : getDifferenceInMinutes(early, newEta);
  };

  switch (brokerageStatus) {
    case "ONHOLD":
    case "A-RTGDE":
    case "DELV":
      return -1;
    case "AVA":
    case "COV":
    case "COVCOM":
    case "DIS":
      diff = calculateDiff(originSchedArriveEarly, originSchedArriveLate);
      break;
    case "LOA":
    case "LOAD":
    case "DEL":
      diff = calculateDiff(destinationSchedArriveEarly, destinationSchedArriveLate);
      break;
    default:
      return -1;
  }

  if (diff > 30) return 1;
  if (diff < 0) return 3;
  return 2;
};


export {
  nullCheck,
  removeDuplicatesFromArray,
  cleanData,
  isDate,
  getEtaStatus,
  getLoadExceptions,
  getEtaStatusHoverString
};